import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentPropsWithoutRef } from 'react';

import { RequiredBadge } from '@/chassis/ui/components';
import DocumentCard from '@/chassis/ui/components/data-display/DocumentCard';
import { buildAuthHeaders, buildFallbackDownloadUrl, parseFileNameFromDisposition } from './helpers';
import { t } from '@/chassis/ui/i18n/i18n-helper';
import type { Language } from '@/chassis/ui/i18n/i18n-helper';
import { i18n as sharedI18n } from '@/chassis/ui/i18n/i18n-commons';
import { useLanguage } from '@/chassis/ui/i18n/useLanguage';

type I18nLabel = Record<Language, string>;

// ============================================
// File Types - Internal Types do Backend
// ============================================

interface FileUploadBaseProps {
    id?: string;
    accept?: string;
    multiple?: boolean;
    disabled?: boolean;
    onChange?: (files: FileList | null) => void;
    onValueChange?: (value: unknown) => void;
    mapFileToValue?: (file: File | null, files: FileList | null) => unknown;
    className?: string;
    maxSize?: number; // in MB
    variant?: 'dropzone' | 'button';
    buttonLabel?: string;
    label?: string;
    i18nLabel?: I18nLabel | string;
    dropLabel?: string;
    i18nDropLabel?: I18nLabel | string;
    required?: boolean;
    i18nRequiredLabel?: I18nLabel | string;
    helpText?: string;
    i18nHelpText?: I18nLabel | string;
    showRequiredBadgeInside?: boolean;
    /** Nome já existente para exibir como selecionado (ex.: edição de registro) */
    valueName?: string | null;
    /** URL para visualizar/baixar o arquivo já existente */
    valueUrl?: string | null;
    /** Callback para remover arquivo existente sem novo upload */
    onRemove?: () => void;
    /** Callback para visualizar arquivo existente; caso não informado, usa URL ou selectedFile */
    onView?: () => void;
    /** Callback para download do arquivo; caso não informado, usa URL ou selectedFile */
    onDownload?: () => void;
}

const FileUploadBase = forwardRef<HTMLInputElement, FileUploadBaseProps>(({
    id,
    accept,
    multiple,
    disabled,
    onChange,
    onValueChange,
    mapFileToValue,
    className = '',
    // 21/10/2026: 15 -> 20 MB. Casado com o limite de corpo do backend, que foi
    // de 15mb para 32mb no mesmo dia. A conta: o arquivo sobe em base64, que
    // infla 1/3, entao 20 MB crus viram ~27mb de JSON e cabem nos 32mb.
    // Antes os dois numeros eram 15 e nao se falavam: a tela aceitava 15 MB e o
    // servidor recusava qualquer coisa acima de ~11 MB.
    maxSize = 20,
    variant = 'dropzone',
    buttonLabel,
    label,
    i18nLabel,
    dropLabel,
    i18nDropLabel,
    required,
    i18nRequiredLabel,
    helpText,
    i18nHelpText,
    showRequiredBadgeInside = false,
    valueName,
    valueUrl,
    onRemove,
    onView,
    onDownload,
}, ref) => {
    const [dragActive, setDragActive] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedName, setSelectedName] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [externalName, setExternalName] = useState<string | null>(valueName ?? null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const lang = useLanguage();

    const resolveLabel = useCallback((value?: I18nLabel | string, fallback?: string) => {
        if (!value) return fallback;
        if (typeof value === 'string') return value;
        return value[lang];
    }, [lang]);

    const fieldLabel = resolveLabel(i18nLabel, label);
    const requiredLabel = required ? resolveLabel(i18nRequiredLabel, t(sharedI18n, 'common.labels.required', lang)) : null;
    const dropText = resolveLabel(i18nDropLabel, dropLabel ?? 'Clique para fazer upload ou arraste e solte');
    const helpLabel = resolveLabel(i18nHelpText, helpText);
    const currentFileName = selectedName || externalName || '';
    const hasDocument = Boolean(selectedFile || selectedName || externalName || valueUrl);
    const shouldShowDropzone = Boolean(multiple) || !hasDocument;

    // Sincroniza nome vindo de fora (edição) com o estado exibido
    useEffect(() => {
        if (valueName === undefined) return;
        if (valueName === externalName) return;
        setExternalName(valueName || null);
        if (!valueName) {
            setSelectedName(null);
            setSelectedFile(null);
        }
    }, [valueName]);

    const setRefs = useCallback(
        (node: HTMLInputElement | null) => {
            inputRef.current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
        },
        [ref],
    );

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const validateFiles = (files: FileList): boolean => {
        setError(null);
        const maxSizeBytes = maxSize * 1024 * 1024;

        for (let i = 0; i < files.length; i++) {
            if (files[i].size > maxSizeBytes) {
                setError(`Arquivo muito grande. Máximo: ${maxSize}MB`);
                return false;
            }
        }
        return true;
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (disabled) return;

        const files = e.dataTransfer.files;
        if (files && files.length > 0 && validateFiles(files)) {
            const file = files[0] ?? null;
            const value = mapFileToValue ? mapFileToValue(file, files) : file;
            onChange?.(files);
            onValueChange?.(value);
            setSelectedFile(file);
            setSelectedName(file?.name ?? null);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0 && validateFiles(files)) {
            const file = files[0] ?? null;
            const value = mapFileToValue ? mapFileToValue(file, files) : file;
            onChange?.(files);
            onValueChange?.(value);
            setSelectedFile(file);
            setSelectedName(file?.name ?? null);
        }
    };

    const handleClear = () => {
        setSelectedFile(null);
        setSelectedName(null);
        setExternalName(null);
        if (inputRef.current) {
            inputRef.current.value = '';
        }

        if (onRemove) {
            onRemove();
            return;
        }

        onChange?.(null);
        onValueChange?.(mapFileToValue ? mapFileToValue(null, null) : null);
    };

    const fetchPersistedFileBlob = async (): Promise<{ blobUrl: string; suggestedFileName: string } | null> => {
        if (!valueUrl) {
            return null;
        }

        let response = await fetch(valueUrl, {
            method: 'GET',
            headers: buildAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok && (response.status === 400 || response.status === 404)) {
            const fallbackUrl = buildFallbackDownloadUrl(valueUrl);
            if (fallbackUrl && fallbackUrl !== valueUrl) {
                const retryResponse = await fetch(fallbackUrl, {
                    method: 'GET',
                    headers: buildAuthHeaders(),
                    credentials: 'include',
                });
                if (retryResponse.ok) {
                    response = retryResponse;
                }
            }
        }

        if (!response.ok) {
            throw new Error(`Falha ao obter arquivo (${response.status})`);
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const headerFileName = parseFileNameFromDisposition(response.headers.get('content-disposition'));

        return {
            blobUrl,
            suggestedFileName: headerFileName || selectedName || externalName || 'arquivo',
        };
    };

    const handleView = async () => {
        if (onView) {
            onView();
            return;
        }

        if (selectedFile) {
            const url = URL.createObjectURL(selectedFile);
            window.open(url, '_blank', 'noopener,noreferrer');
            setTimeout(() => URL.revokeObjectURL(url), 5000);
            return;
        }

        try {
            const resolved = await fetchPersistedFileBlob();
            if (!resolved?.blobUrl) return;
            window.open(resolved.blobUrl, '_blank', 'noopener,noreferrer')?.focus?.();
            setTimeout(() => URL.revokeObjectURL(resolved.blobUrl), 60_000);
        } catch (err) {
            console.error('Não foi possível abrir o arquivo.', err);
        }
    };

    const handleDownload = async () => {
        if (onDownload) {
            onDownload();
            return;
        }

        if (selectedFile) {
            const url = URL.createObjectURL(selectedFile);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = selectedName || externalName || 'arquivo';
            anchor.target = '_blank';
            anchor.rel = 'noreferrer';
            anchor.click();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
            return;
        }

        try {
            const resolved = await fetchPersistedFileBlob();
            if (!resolved?.blobUrl) return;
            const { blobUrl, suggestedFileName } = resolved;
            const anchor = document.createElement('a');
            anchor.href = blobUrl;
            anchor.download = suggestedFileName;
            anchor.target = '_blank';
            anchor.rel = 'noreferrer';
            anchor.click();
            setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
            return;
        } catch (err) {
            console.error('Não foi possível baixar o arquivo.', err);
            return;
        }
    };

    if (variant === 'button') {
        return (
            <div className={className}>
                {(fieldLabel || requiredLabel) && (
                    <div className="mb-1 flex items-center gap-2 text-sm text-slate-800">
                        {fieldLabel ? <span>{fieldLabel}</span> : null}
                        {requiredLabel ? <RequiredBadge /> : null}
                    </div>
                )}
                <input ref={setRefs} id={id} type="file" accept={accept} multiple={multiple} disabled={disabled} required={required} onChange={handleChange} className="hidden" />
                {!selectedName && !externalName ? (
                    <button
                        type="button"
                        onClick={() => {
                            if (disabled) return;
                            inputRef.current?.click();
                        }}
                        className={`inline-flex w-full items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-medium shadow-sm transition-colors ${
                            disabled
                                ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                                : 'border-slate-200 bg-white text-slate-800 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                    >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M12 5v14" />
                            <path d="M5 12h14" />
                        </svg>
                        <span className="flex items-center gap-1">
                            {buttonLabel || 'Selecionar arquivo'}
                            {showRequiredBadgeInside && requiredLabel ? <RequiredBadge /> : null}
                        </span>
                    </button>
                ) : (
                    <div className="flex w-full items-stretch">
                        <input
                            type="text"
                            value={selectedName || externalName || ''}
                            readOnly
                            className="w-full rounded-l-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 shadow-sm focus:outline-none"
                        />
                        <button
                            type="button"
                            onClick={handleDownload}
                            disabled={!selectedFile && !valueUrl}
                            className={`-ml-px inline-flex items-center justify-center border px-3 py-2 text-sm shadow-sm transition-colors ${
                                !selectedFile && !valueUrl
                                    ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                                    : 'border-slate-200 bg-white text-slate-800 hover:border-slate-300 hover:bg-slate-50'
                            }`}
                            aria-label="Baixar arquivo"
                        >
                            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" x2="12" y1="15" y2="3" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            onClick={handleView}
                            disabled={!selectedFile && !valueUrl}
                            className={`-ml-px inline-flex items-center justify-center rounded-none border px-3 py-2 text-sm shadow-sm transition-colors ${
                                !selectedFile && !valueUrl
                                    ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                                    : 'border-slate-200 bg-white text-slate-800 hover:border-slate-300 hover:bg-slate-50'
                            }`}
                            aria-label="Visualizar arquivo"
                        >
                            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="12" cy="12" r="3" />
                                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            onClick={handleClear}
                            className="-ml-px inline-flex items-center justify-center rounded-r-md border border-rose-200 px-3 py-2 text-sm text-rose-700 shadow-sm transition-colors hover:border-rose-300 hover:bg-rose-50"
                            aria-label="Limpar arquivo"
                        >
                            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M18 6 6 18" />
                                <path d="m6 6 12 12" />
                            </svg>
                        </button>
                    </div>
                )}
                {helpLabel && <p className="mt-1 text-xs text-slate-500">{helpLabel}</p>}
                {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
            </div>
        );
    }

    return (
        <div className={className}>
            {(fieldLabel || requiredLabel) && (
                <div className="mb-1 flex items-center gap-2 text-sm text-slate-800">
                    {fieldLabel ? <span>{fieldLabel}</span> : null}
                    {requiredLabel ? <RequiredBadge /> : null}
                </div>
            )}
            {shouldShowDropzone ? (
                <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    className={`
                relative border-2 border-dashed rounded-lg p-6 text-center
                ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-blue-400'}
                transition-colors
              `}
                >
                    <input
                        ref={setRefs}
                        id={id}
                        type="file"
                        accept={accept}
                        multiple={multiple}
                        disabled={disabled}
                        required={required}
                        onChange={handleChange}
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                    />
                    <div className="space-y-2">
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                            <path
                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                            <span className="font-medium text-blue-600">{dropText}</span>
                            {showRequiredBadgeInside && requiredLabel ? <RequiredBadge /> : null}
                        </div>
                        {accept ? <p className="text-xs text-gray-500">Formatos aceitos: {accept}</p> : null}
                        <p className="text-xs text-gray-500">Tamanho máximo: {maxSize}MB</p>
                    </div>
                </div>
            ) : null}

            {shouldShowDropzone && helpLabel ? <p className="mt-2 text-xs text-slate-500">{helpLabel}</p> : null}
            {hasDocument ? (
                <DocumentCard
                    title={currentFileName || 'documento'}
                    fileName={currentFileName || undefined}
                    icon="FileText"
                    onOpen={handleView}
                    onDownload={handleDownload}
                    onRemove={handleClear}
                />
            ) : null}
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>
    );
});
FileUploadBase.displayName = 'FileUploadBase';

// File.Video
interface FileVideoProps extends Omit<FileUploadBaseProps, 'accept'> {}

export const FileVideo = forwardRef<HTMLInputElement, FileVideoProps>((props, ref) => {
    return <FileUploadBase {...props} ref={ref} accept="video/*,.mp4,.avi,.mov,.wmv,.flv,.mkv" />;
});
FileVideo.displayName = 'FileVideo';

// File.Audio
interface FileAudioProps extends Omit<FileUploadBaseProps, 'accept'> {}

export const FileAudio = forwardRef<HTMLInputElement, FileAudioProps>((props, ref) => {
    return <FileUploadBase {...props} ref={ref} accept="audio/*,.mp3,.wav,.ogg,.aac,.flac" />;
});
FileAudio.displayName = 'FileAudio';

// File.Image
interface FileImageProps extends Omit<FileUploadBaseProps, 'accept'> {}

export const FileImage = forwardRef<HTMLInputElement, FileImageProps>((props, ref) => {
    return <FileUploadBase {...props} ref={ref} accept="image/*,.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg" />;
});
FileImage.displayName = 'FileImage';

// File.Document
interface FileDocumentProps extends Omit<FileUploadBaseProps, 'accept'> {}

export const FileDocument = forwardRef<HTMLInputElement, FileDocumentProps>((props, ref) => {
    return <FileUploadBase {...props} ref={ref} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" />;
});
FileDocument.displayName = 'FileDocument';

// Generic File (all types)
interface FileGenericProps extends FileUploadBaseProps {}

export const FileGeneric = forwardRef<HTMLInputElement, FileGenericProps>((props, ref) => {
    return <FileUploadBase {...props} ref={ref} />;
});
FileGeneric.displayName = 'FileGeneric';
